/**
 * The company's peers from the corpus - Companies House filings read by code and scored by their
 * own tags - and where its last actual year sits among them, ratio by ratio. It values nothing:
 * it places the company in its sector's distribution. Each peer is a company number a user can
 * look up, and take out; the distribution is then recomputed without it.
 */

import type { PeersComputed } from '../../../types/financial-model-v2';

const LABELS: Record<string, [string, 'pct' | 'days' | 'x']> = {
  revenueGrowth: ['Revenue growth', 'pct'],
  grossMargin: ['Gross margin', 'pct'],
  ebitdaMargin: ['EBITDA margin', 'pct'],
  operatingMargin: ['Operating margin', 'pct'],
  netMargin: ['Net margin', 'pct'],
  debtorDays: ['Debtor days', 'days'],
  creditorDays: ['Creditor days', 'days'],
  stockDays: ['Stock days', 'days'],
  workingCapitalToRevenue: ['Working capital / revenue', 'pct'],
  cashConversion: ['Cash conversion', 'x'],
  capexToRevenue: ['Capex / revenue', 'pct'],
  capexToDepreciation: ['Capex / depreciation', 'x'],
  currentRatio: ['Current ratio', 'x'],
};

function fmt(value: number | null | undefined, kind: 'pct' | 'days' | 'x'): string {
  if (value === null || value === undefined) return '-';
  if (kind === 'pct') return `${(value * 100).toFixed(1)}%`;
  if (kind === 'days') return value.toFixed(0);
  return `${value.toFixed(2)}x`;
}

export function PeersPanel({
  peers,
  onExclude,
}: {
  peers: PeersComputed | null | undefined;
  onExclude?: (companyNumber: string) => void;
}) {
  if (!peers) return null;
  const rows = Object.entries(peers.ratios ?? {}).filter(([name]) => name in LABELS);
  return (
    <section className="mt-6 rounded-xl border border-gray-200 bg-white p-4" data-testid="peers-panel">
      <h3 className="text-sm font-semibold text-gray-900">Peers from the corpus</h3>
      <p className="mt-0.5 text-xs text-gray-600">
        {peers.note}
        {peers.year ? ` - against FY${peers.year}` : ''}
      </p>
      {rows.length > 0 && (
        <table className="mt-3 w-full text-xs">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="py-1 font-medium">Ratio</th>
              <th className="py-1 text-right font-medium">This company</th>
              <th className="py-1 text-right font-medium">Lower quartile</th>
              <th className="py-1 text-right font-medium">Median</th>
              <th className="py-1 text-right font-medium">Upper quartile</th>
              <th className="py-1 text-right font-medium">Percentile</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map(([name, r]) => {
              const [label, kind] = LABELS[name];
              return (
                <tr key={name}>
                  <td className="py-1 text-gray-800">{label}</td>
                  <td className="py-1 text-right font-mono">{fmt(r.subject, kind)}</td>
                  <td className="py-1 text-right font-mono">{fmt(r.p25, kind)}</td>
                  <td className="py-1 text-right font-mono">{fmt(r.p50, kind)}</td>
                  <td className="py-1 text-right font-mono">{fmt(r.p75, kind)}</td>
                  <td className="py-1 text-right font-mono">
                    {r.percentile === null || r.percentile === undefined ? '-' : `${Math.round(r.percentile * 100)}`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {(peers.members?.length ?? 0) > 0 && (
        <details className="mt-3 text-xs text-gray-700">
          <summary className="cursor-pointer text-gray-600">The {peers.members!.length} peers (company numbers)</summary>
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {peers.members!.map((number) => (
              <li key={number} className="rounded bg-gray-100 px-1.5 py-0.5 font-mono">
                {number}
                {onExclude && (
                  <button
                    type="button"
                    className="ml-1 text-gray-400 hover:text-red-600"
                    aria-label={`Take ${number} out of the peers`}
                    onClick={() => onExclude(number)}
                  >
                    ×
                  </button>
                )}
              </li>
            ))}
          </ul>
          {(peers.excluded?.length ?? 0) > 0 && (
            <p className="mt-2 text-gray-500">Taken out: {peers.excluded!.join(', ')}</p>
          )}
        </details>
      )}
      {peers.source && <p className="mt-2 text-[11px] text-gray-400">{peers.source}</p>}
    </section>
  );
}
